<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <!-- Form -->
        <b-form
            ref="newsitemform"
            name="newsitemForm"
            v-on:submit.prevent="$_onSubmit"
            class="pb-5"
        >
            <div class="col-xs-8 col-md-5">
                <!-- Date -->
                <div class="form-group">
                    <div>
                        <base-input
                            id="datepicker"
                            :text="labels.date"
                            :plaintext="!userInfo.isSuper"
                            type="date"
                            v-model="record.date"
                            :required="userInfo.isSuper"
                            autofocus
                        >
                        </base-input>
                    </div>
                </div>
                <!-- Priority -->
                <div class="form-group">
                    <div>
                        <BaseSelect
                            :name="'prioritytext'"
                            :text="labels.priority"
                            :disabled="!userInfo.isSuper"
                            :extendedClass="{ plaintext: !userInfo.isSuper }"
                            :required="true"
                            v-show="priorityTypes"
                            :value="priorityCode"
                            :options="priorityTypes"
                            @selected="$_onPrioritySelected"
                        >
                        </BaseSelect>
                    </div>
                </div>
                <!-- Title -->
                <div class="form-group">
                    <TextInput
                        :text="labels.title"
                        v-model="record.title"
                        :name="'news-title'"
                        :invalid="nameMessage"
                        :valid="nameMessage"
                        :plaintext="!userInfo.isSuper"
                        :required="userInfo.isSuper"
                    >
                    </TextInput>
                </div>
            </div>
            <div class="col-xs-8 col-md-8">
                <!-- Newsitem, can be highlighted and filtered based on searchTerm -->
                <div v-show="!changeNewsitem">
                    <div class="row">
                        <!-- Search field -->
                        <search-box
                            @input="$_onInputSearch"
                            :info="$t('searchInfo')"
                            :value="localSearchtext"
                        >
                        </search-box>
                        <!-- Newsitem bijwerken, options for superuser only to switch to input mode -->
                        <div
                            class="form-group"
                            v-show="userInfo.isSuper && mode === modeEditConst"
                        >
                            <base-button
                                @click="changeNewsitem = true"
                                :text="labels.update"
                                :iconClass="`fa fa-pencil-alt`"
                            >
                            </base-button>
                        </div>
                    </div>
                    <div>
                        <label class="control-label">{{
                            labels.newsitem
                        }}</label>
                        <br />
                        <!-- Show news, highlighting the search text -->
                        <div v-html="$_highlight()"></div>
                    </div>
                </div>
                <!-- Input news for superuser (when switched to input mode) -->
                <div v-show="changeNewsitem">
                    <x-textarea
                        :name="'newsItem'"
                        :text="labels.newsitem"
                        :rows="20"
                        :cols="140"
                        class="mb-5"
                        v-model="record.news"
                        :hasResizeHandle="true"
                        :showCounter="true"
                        :plaintext="!userInfo.isSuper"
                        :maxlength="8000"
                        :required="userInfo.isSuper"
                    >
                    </x-textarea>
                </div>
                <div v-show="userInfo.isSuper">
                    <div class="sticky-bottom fixed-bottom">
                        <div class="btn-group" role="group">
                            <b-button
                                type="submit"
                                id="btnSaveNewsItems"
                                name="btnSaveNewsItems"
                                variant="primary"
                                :disabled="inProgress"
                            >
                                {{ $t('btnSaveLabel') }}
                                <span
                                    :class="{
                                        'fa fa-check': !isSaving,
                                        'fas fa-sync fa-spin': isSaving
                                    }"
                                ></span>
                            </b-button>
                            <b-button
                                id="btnSendNewsItems"
                                name="btnSendNewsItems"
                                @click="$_onSend"
                                class="btn-space-5"
                                :disabled="inProgress"
                            >
                                {{ labels.send }}
                                <span
                                    class="glyphicon"
                                    v-bind:class="{
                                        'fas fa-envelope': !isSending,
                                        'fas fa-sync': isSending
                                    }"
                                ></span>
                            </b-button>
                            <b-button
                                id="btnCancelNewsItems"
                                name="btnCancelNewsItems"
                                @click="$_onCancel"
                                class="btn-space-5"
                                :disabled="isSaving || isDeleting"
                            >
                                <span class="fas fa-times"></span>
                                {{ $t('btnCancelLabel') }}
                            </b-button>
                        </div>
                        <div
                            class="btn-group btn-space-10"
                            role="group"
                            v-show="mode === modeEditConst"
                        >
                            <b-button
                                id="btnDeleteNewsItems"
                                name="btnDeleteNewsItems"
                                @click="$_onRemove"
                                :disabled="inProgress"
                            >
                                {{ $t('btnDeleteLabel') }}
                                <span
                                    :class="{
                                        'fa fa-trash-alt': !isDeleting,
                                        'fas fa-sync fa-spin': isDeleting
                                    }"
                                ></span>
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </b-form>
    </div>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import { MODE_ADD, MODE_EDIT, PRIORITY } from '../../constants';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import TextInput from '../../components/BaseComponents/x-input.vue';
    import XTextarea from '../../components/BaseComponents/x-textarea.vue';
    import SearchBox from '../../components/SearchBox.vue';
    import BaseSelect from '../../components/BaseComponents/base-select.vue';
    import BaseButton from '../../components/BaseComponents/base-button.vue';
    import BaseInput from '../../components/BaseComponents/base-input.vue';
    import refcodeMixin from '../../components/Mixins/Refcodes/refcodeMixin.vue';

    export default {
        mixins: [modalMixin, refcodeMixin],

        components: {
            TextInput,
            'search-box': SearchBox,
            'x-textarea': XTextarea,
            'base-button': BaseButton,
            'base-input': BaseInput,
            BaseSelect
        },

        props: {
            id: { type: Number, required: false, default: -1 },
            searchtext: { type: String, required: false, default: '' }
        },

        data: function () {
            return {
                // Constants
                modeEditConst: MODE_EDIT,

                userInfo: {},
                record: {},

                mode: 0,
                title: '',

                changeNewsitem: false,

                nameMessage: '',
                formattedNews: '',

                priorityTypes: [],
                priorityCode: null,

                isSaving: false,
                isSending: false,
                isDeleting: false,

                labels: {
                    date: this.$t('columnNameDate'),
                    priority: this.$t('columnNamePriority'),
                    title: this.$t('columnNameTitle'),
                    newsitem: this.$t('columnNameNewsItem'),
                    update: this.$t('labelNewsItemUpdate'),
                    send: this.$t('buttonNameSend')
                },
                localSearchtext: ''
            };
        },

        created() {
            this.userInfo = this.$store.getters.userInfo;
        },

        computed: {
            inProgress() {
                return this.isSaving || this.isDeleting || this.isSending;
            }
        },

        async mounted() {
            if (this.id && this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('newsItemDetailEditTitle');
                nprogress.start();

                // Get existing data from the backend
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetNewsItem',
                    parameters: { id: this.id }
                });

                this.record = { ...result?.data };

                // Take date only
                this.record.date = this.record.date.split('T')[0];

                // Filter the notes, only show lines that contain the searchTerm
                this.localSearchtext = this.searchtext.slice(0);
                this.$_filterNews();

                // Retrieve Priority types
                this.$_initializePriorityTypes();
                this.priorityCode = this.record.priority;
                nprogress.done();
            } else {
                this.mode = MODE_ADD;
                this.title = this.$t('newsItemDetailAddTitle');
                this.changeNewsitem = true;

                // Retrieve Priority types
                this.$_initializePriorityTypes();
            }
        },

        methods: {
            /**
             * Priority selected event handler
             * @param  {[type]} value [description]
             */
            $_onPrioritySelected(value) {
                this.priorityCode = value;
            },

            /**
             *  Filted the newsItems based on searchtext
             */
            $_filterNews() {
                // In case of empty searchtext, formatted notes are equal to original notes
                if (this.localSearchtext === '') {
                    this.formattedNews = this.record.news;
                } else {
                    // If there is a searchtext, we filter the array
                    // Split the newsitem on newline
                    let lineArray = this.record.news.split('\n');

                    let upperText = this.localSearchtext.toUpperCase();

                    // Filter out the lines that do not include the searchtext
                    let filteredLineArray = _.filter(lineArray, function (line) {
                        return line.toUpperCase().includes(upperText);
                    });

                    // Recreate string for display
                    let tempString = '';

                    filteredLineArray.forEach(function (filteredLine) {
                        tempString += filteredLine;
                        tempString += '\n\n';
                    });

                    // Get rid of final 2 newlines
                    tempString = tempString.substring(0, tempString.length - 2);

                    // Assign to scope variable which is used for display
                    this.formattedNews = tempString;
                }
            },

            /**
             * Filter for highlighting the searchtext in de newsitems
             * @param {string} text newsItem text
             * @return {string}     a paragraph with the highlighted text
             */
            $_highlight: function () {
                let text = this.formattedNews;
                const phrase = this.localSearchtext;

                // Create paragraph tags
                let newsItemOpenParagraph = '<p style="white-space: pre-line;">';
                let newsItemCloseParagraph = '</p>';

                // Escape special characters for regex
                // See: https://stackoverflow.com/questions/3115150/how-to-escape-regular-expression-special-characters-using-javascript
                let escapedRegexPhrase = phrase.replace(
                    /[-[\]{}()*+?.,\\^$|#\s]/g,
                    '\\$&'
                );

                // Replace phrase with a highlighted span
                if (escapedRegexPhrase && text) {
                    text = text.replace(
                        new RegExp(`(${escapedRegexPhrase})`, 'gi'),
                        '<span class="highlighted">$1</span>'
                    );
                }

                // Recreate the paragraph HTML and return
                return newsItemOpenParagraph + text + newsItemCloseParagraph;
            },

            $_initializePriorityTypes() {
                // Retrieve Authentication types
                this.getRefCodes(PRIORITY.KEY).then((result) => {
                    for (const priorityType of result.data) {
                        this.priorityTypes.push({
                            value: priorityType.code,
                            text: priorityType.description
                        });
                    }
                });
            },

            $_onInputSearch(newValue) {
                this.localSearchtext = newValue;
                this.$_filterNews();
            },

            $_gotoOverview() {
                this.$router.push({ path: '/dashboard/newsitems/newsitems' });
            },

            $_onSubmit() {
                if (this.$refs.newsitemform.checkValidity()) {
                    this.$_onSave(false);
                }
            },

            async $_onSave() {
                try {
                    this.isSaving = true;
                    const newsitem = Object.assign({}, this.record);
                    newsitem.priority = this.priorityCode;
                    nprogress.start();

                    // Save the newsitem
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'SaveNewsItem',
                        data: newsitem
                    });

                    if (result) {
                        const data = {
                            message: this.$t('newsItemCardSavedConfirmation'),
                            variant: 'success',
                            autoHideDelay: 2500
                        };

                        if (this.mode !== MODE_EDIT) {
                            this.showToast(data);
                            this.$_gotoOverview();
                            this.isSaving = false;
                        } else {
                            this.showToast(data);
                            this.isSaving = false;
                        }
                    }
                    nprogress.done();
                } catch (error) {
                    nprogress.done();
                    // toaster show error
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('saveFailureTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                    this.isSaving = false;
                }
            },

            $_onCancel() {
                this.$_gotoOverview();
            },

            async $_onRemove() {
                try {
                    const msgConfirm = {
                        message: this.$t('newsItemCardDeletionsConfirmation')
                    };
                    const confirm = await this.msgBoxConfirm(msgConfirm);
                    if (confirm) {
                        this.isDeleting = true;

                        const result = await this.$store.dispatch('useApiData', {
                            name: 'DeleteNewsItem',
                            parameters: { id: this.record.id }
                        });
                        if (result) {
                            this.$_gotoOverview();
                            this.isDeleting = false;
                        }
                    }
                } catch (error) {
                    const data = {
                        message: this.$t('deleteFailureMessage', [
                            `${error.response.data.message}`
                        ]),
                        variant: 'danger',
                        title: this.$t('deleteFailureTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                    this.isDeleting = false;
                }
            },

            async $_onSend() {
                try {
                    if (!this.$refs.newsitemform.checkValidity()) {
                        return;
                    }

                    this.isSending = true;
                    const newsitem = Object.assign({}, this.record);

                    // Send the newsitem
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'SendNewsItem',
                        data: newsitem
                    });
                    if (result) {
                        if (result.id !== 0 && this.mode !== MODE_EDIT) {
                            this.showToast({
                                message: this.$t('newsItemSendConfirmation'),
                                variant: 'success',
                                autoHideDelay: 2500
                            });
                            this.$_gotoOverview();
                            this.isSaving = false;
                        } else if (result.id !== 0) {
                            this.showToast({
                                message: this.$t('newsItemSendConfirmation'),
                                variant: 'success',
                                autoHideDelay: 2500
                            });
                            this.isSending = false;
                        }
                    }
                } catch (error) {
                    // toaster show error
                    const data = {
                        message: this.$t('newsItemSendFailureMessage', [
                            `${error.response.data.message}`
                        ]),
                        variant: 'danger',
                        title: this.$t('newsItemSendFailure'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                    this.isSending = false;
                }
            }
        }
    };
</script>
